import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Inline confirmation strip rendered *inside* a modal when the user attempts
 * to close it with unsaved changes. Looks like a banner pinned to the top —
 * not a stacked dialog. Keeps focus inside the host modal.
 *
 * Light variant matches the in-modal palette; dark variant is for the dark
 * admin-panel modals (e.g., AccessRequest, AddAdvisor, Feedback).
 */
const InlineConfirmCloseBar = ({ onDiscard, onKeep, variant = 'light', message }) => {
  const isDark = variant === 'dark';
  const wrap = isDark
    ? 'bg-amber-900/30 border-amber-700/40 text-amber-100'
    : 'bg-amber-50 border-amber-200 text-amber-900';
  const keepBtn = isDark
    ? 'bg-gray-700 hover:bg-gray-600 text-white'
    : 'bg-white hover:bg-slate-50 border border-slate-300 text-slate-700';
  const discardBtn = 'bg-rose-600 hover:bg-rose-700 text-white';

  return (
    <div
      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 rounded-md border ${wrap}`}
      data-testid="dirty-confirm-bar"
      role="alertdialog"
      aria-live="polite"
    >
      <div className="flex items-start gap-2 text-sm">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
        <span>{message || 'You have unsaved changes. Discard them and close?'}</span>
      </div>
      <div className="flex gap-2 self-stretch sm:self-auto">
        <button
          type="button"
          onClick={onKeep}
          data-testid="dirty-confirm-keep"
          className={`text-xs sm:text-sm px-3 py-1.5 rounded-md font-medium ${keepBtn}`}
        >
          Keep editing
        </button>
        <button
          type="button"
          onClick={onDiscard}
          data-testid="dirty-confirm-discard"
          className={`text-xs sm:text-sm px-3 py-1.5 rounded-md font-medium ${discardBtn}`}
        >
          Discard &amp; close
        </button>
      </div>
    </div>
  );
};

export default InlineConfirmCloseBar;
